// Messaging state: the thread list, the open thread's messages, sends and coalesced live refreshes.
// Server truth only: a sent message appears when the server returns it (state `queued`), and every
// later state (sent, delivered, failed) arrives from the server, never from a timer here.
import type { EventEnvelope } from '@bcio-voice/contracts';
import { type MessagingClient, type Message, type MessageThread, isAbortError, isApiRequestError } from '../../lib/api.ts';
import { type ErrorView, describeError } from '../../lib/errorCopy.ts';
import { messagingEventDecision } from './live.ts';
import { mergeMessages, mergeThreads, readMessage, readRows, readThread, unwrap } from './model.ts';

export interface ListState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  rows: MessageThread[];
  nextCursor: string | null;
  loadingMore: boolean;
  error: ErrorView | null;
  /** Rows this version couldn't read. */
  skipped: number;
}

export interface ThreadView {
  threadId: string;
  status: 'loading' | 'ready' | 'error';
  messages: Message[];
  nextCursor: string | null;
  loadingEarlier: boolean;
  error: ErrorView | null;
  skipped: number;
}

export interface MessagingSnapshot {
  list: ListState;
  thread: ThreadView | null;
}

export type SendResult =
  | { ok: true; message: Message }
  | { ok: false; error: { code: string; status: number; outcomeUnknown: boolean; requestId: string | null } };

const PAGE = 50;
/** Events within this window collapse into one refetch per resource. */
export const COALESCE_MS = 400;

type Timer = unknown;

export class MessagingController {
  private snapshot: MessagingSnapshot = {
    list: { status: 'idle', rows: [], nextCursor: null, loadingMore: false, error: null, skipped: 0 },
    thread: null,
  };
  private readonly listeners = new Set<() => void>();
  private listRequest = 0;
  private threadRequest = 0;
  private readonly timers = new Map<string, Timer>();
  private readonly inFlight = new Set<string>();
  private readonly dirty = new Set<string>();
  /** Stopped controllers schedule no refreshes. State still updates, so a remount (StrictMode) works. */
  private stopped = false;
  private readonly setTimer: (fn: () => void, ms: number) => Timer;
  private readonly clearTimer: (t: Timer) => void;

  constructor(
    private readonly client: MessagingClient,
    options: { setTimer?: (fn: () => void, ms: number) => Timer; clearTimer?: (t: Timer) => void } = {},
  ) {
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? (t => clearTimeout(t as ReturnType<typeof setTimeout>));
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): MessagingSnapshot => this.snapshot;

  private set(patch: Partial<MessagingSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of [...this.listeners]) listener();
  }

  private setList(patch: Partial<ListState>): void {
    this.set({ list: { ...this.snapshot.list, ...patch } });
  }

  private setThread(threadId: string, patch: Partial<ThreadView>): void {
    const current = this.snapshot.thread;
    if (!current || current.threadId !== threadId) return;
    this.set({ thread: { ...current, ...patch } });
  }

  /** (Re)starts live refreshes; the page calls it on mount. */
  activate(): void {
    this.stopped = false;
  }

  /** Stops live refreshes and drops pending timers; the page calls it on unmount. */
  dispose(): void {
    this.stopped = true;
    for (const t of this.timers.values()) this.clearTimer(t);
    this.timers.clear();
    this.dirty.clear();
  }

  thread(id: string): MessageThread | null {
    return this.snapshot.list.rows.find(t => t.id === id) ?? null;
  }

  // ---- Thread list ------------------------------------------------------------------------------

  /** First page. A reload keeps rows from later pages and merges the fresh first page over them. */
  async loadThreads(options: { keepRows?: boolean } = {}): Promise<void> {
    const id = ++this.listRequest;
    if (!options.keepRows) this.setList({ status: 'loading', error: null });
    try {
      const body = await this.client.threads({ limit: PAGE });
      if (id !== this.listRequest) return;
      const read = readRows(body, readThread);
      if (!read) throw invalid();
      const rows = options.keepRows ? mergeThreads(this.snapshot.list.rows, read.rows) : mergeThreads([], read.rows);
      this.setList({ status: 'ready', rows, nextCursor: options.keepRows && this.snapshot.list.nextCursor ? this.snapshot.list.nextCursor : read.nextCursor, error: null, skipped: read.skipped });
    } catch (error) {
      if (id !== this.listRequest || isAbortError(error)) return;
      const view = describeError(error, 'Couldn’t load conversations');
      // A failed background refresh keeps the rows already shown.
      if (options.keepRows && this.snapshot.list.status === 'ready') this.setList({ error: view });
      else this.setList({ status: 'error', error: view });
    }
  }

  async loadMoreThreads(): Promise<void> {
    const { nextCursor, loadingMore } = this.snapshot.list;
    if (!nextCursor || loadingMore) return;
    this.setList({ loadingMore: true, error: null });
    try {
      const body = await this.client.threads({ limit: PAGE, cursor: nextCursor });
      const read = readRows(body, readThread);
      if (!read) throw invalid();
      this.setList({ rows: mergeThreads(this.snapshot.list.rows, read.rows), nextCursor: read.nextCursor, loadingMore: false, skipped: this.snapshot.list.skipped + read.skipped });
    } catch (error) {
      if (isAbortError(error)) return;
      this.setList({ loadingMore: false, error: describeError(error, 'Couldn’t load more conversations') });
    }
  }

  // ---- One thread -------------------------------------------------------------------------------

  openThread(threadId: string | null): void {
    if (!threadId) {
      this.threadRequest += 1;
      this.set({ thread: null });
      return;
    }
    if (this.snapshot.thread?.threadId === threadId) return;
    this.set({ thread: { threadId, status: 'loading', messages: [], nextCursor: null, loadingEarlier: false, error: null, skipped: 0 } });
    void this.loadMessages(threadId);
  }

  /** The newest page, merged into what's loaded (a refresh never drops earlier pages). */
  async loadMessages(threadId: string): Promise<void> {
    const id = ++this.threadRequest;
    try {
      const body = await this.client.messages(threadId, { limit: PAGE });
      if (id !== this.threadRequest || this.snapshot.thread?.threadId !== threadId) return;
      const read = readRows(body, readMessage);
      if (!read) throw invalid();
      const current = this.snapshot.thread;
      const fresh = read.rows.filter(m => m.thread_id === threadId);
      const first = current.status !== 'ready';
      this.setThread(threadId, {
        status: 'ready',
        messages: mergeMessages(current.messages, fresh),
        nextCursor: first ? read.nextCursor : current.nextCursor,
        error: null,
        skipped: first ? read.skipped + (read.rows.length - fresh.length) : current.skipped,
      });
    } catch (error) {
      if (id !== this.threadRequest || isAbortError(error)) return;
      const view = describeError(error, 'Couldn’t load this conversation');
      if (this.snapshot.thread?.status === 'ready') this.setThread(threadId, { error: view });
      else this.setThread(threadId, { status: 'error', error: view });
    }
  }

  async loadEarlier(): Promise<void> {
    const thread = this.snapshot.thread;
    if (!thread || !thread.nextCursor || thread.loadingEarlier) return;
    const threadId = thread.threadId;
    this.setThread(threadId, { loadingEarlier: true, error: null });
    try {
      const body = await this.client.messages(threadId, { limit: PAGE, cursor: thread.nextCursor });
      const read = readRows(body, readMessage);
      if (!read) throw invalid();
      const current = this.snapshot.thread;
      if (!current || current.threadId !== threadId) return;
      this.setThread(threadId, { messages: mergeMessages(current.messages, read.rows.filter(m => m.thread_id === threadId)), nextCursor: read.nextCursor, loadingEarlier: false, skipped: current.skipped + read.skipped });
    } catch (error) {
      if (isAbortError(error)) return;
      this.setThread(threadId, { loadingEarlier: false, error: describeError(error, 'Couldn’t load earlier messages') });
    }
  }

  retryThread(): void {
    const thread = this.snapshot.thread;
    if (!thread) return;
    this.setThread(thread.threadId, { status: thread.status === 'ready' ? 'ready' : 'loading', error: null });
    void this.loadMessages(thread.threadId);
  }

  // ---- Sending ----------------------------------------------------------------------------------

  /** One explicit send. The message shown is the server's (state `queued`); nothing is invented. */
  async send(threadId: string, body: string, idempotencyKey: string): Promise<SendResult> {
    try {
      const env = await this.client.send(threadId, body, idempotencyKey);
      const message = readMessage(unwrap(env));
      if (!message) throw invalid();
      const current = this.snapshot.thread;
      if (current && current.threadId === threadId) this.setThread(threadId, { messages: mergeMessages(current.messages, [message]) });
      this.schedule('list');
      return { ok: true, message };
    } catch (error) {
      return { ok: false, error: errorFacts(error) };
    }
  }

  /** New conversation: creates or reuses the thread for (line, number). Returns the server's message. */
  async startThread(input: { number_id: string; remote_e164: string; body: string }, idempotencyKey: string): Promise<SendResult> {
    try {
      const env = await this.client.startThread(input, idempotencyKey);
      const message = readMessage(unwrap(env));
      if (!message) throw invalid();
      await this.loadThreads({ keepRows: true });
      return { ok: true, message };
    } catch (error) {
      return { ok: false, error: errorFacts(error) };
    }
  }

  // ---- Live updates -----------------------------------------------------------------------------

  /** A server event: refetch only what it names, coalesced. Unrelated events do nothing. */
  onEvent(event: Pick<EventEnvelope, 'type' | 'aggregate' | 'data'>): void {
    const decision = messagingEventDecision(event);
    if (!decision) return;
    this.schedule('list');
    const open = this.snapshot.thread?.threadId ?? null;
    if (!open) return;
    // A message event without its thread id may belong to the open thread; refresh it to be safe.
    if (decision.threadId === open || (decision.kind === 'message' && decision.threadId === null)) this.schedule(`thread:${open}`);
  }

  /** The stream's replay window expired: reload the list and the open thread once. */
  onResync(): void {
    this.schedule('list');
    const open = this.snapshot.thread?.threadId;
    if (open) this.schedule(`thread:${open}`);
  }

  /** At most one pending timer and one request in flight per resource; extra events mark it dirty. */
  private schedule(key: string): void {
    if (this.stopped) return;
    if (this.inFlight.has(key)) {
      this.dirty.add(key);
      return;
    }
    if (this.timers.has(key)) return;
    this.timers.set(key, this.setTimer(() => {
      this.timers.delete(key);
      void this.run(key);
    }, COALESCE_MS));
  }

  private async run(key: string): Promise<void> {
    if (this.stopped) return;
    this.inFlight.add(key);
    try {
      if (key === 'list') await this.loadThreads({ keepRows: true });
      else if (key.startsWith('thread:')) {
        const threadId = key.slice('thread:'.length);
        if (this.snapshot.thread?.threadId === threadId) await this.loadMessages(threadId);
      }
    } finally {
      this.inFlight.delete(key);
      if (this.dirty.delete(key)) this.schedule(key);
    }
  }

  /** Pending refresh keys (tests and diagnostics). */
  pending(): string[] {
    return [...this.timers.keys(), ...this.inFlight].sort();
  }
}

function invalid(): Error {
  return Object.assign(new Error('The server sent a response this version can’t read.'), { code: 'invalid_response' });
}

function errorFacts(error: unknown): { code: string; status: number; outcomeUnknown: boolean; requestId: string | null } {
  if (isApiRequestError(error)) return { code: error.code, status: error.status, outcomeUnknown: error.outcomeUnknown, requestId: error.requestId };
  const code = typeof error === 'object' && error !== null && typeof (error as { code?: unknown }).code === 'string' ? (error as { code: string }).code : 'client_error';
  // An unreadable reply to a send may still mean the server queued it: treat it as unknown.
  return { code, status: 0, outcomeUnknown: code === 'invalid_response', requestId: null };
}
